// Studio Chat Service — thin wrapper around the generic ChatService.
// Owns: persona + recipe state, persona welcome, modification execution.
// Delegates: intent classification, response generation, history management.
//
// External API (consumed by RecipeReviewPage) is preserved exactly:
//   initialize(), getWelcomeMessage(), getPersonaIntro(), getPersonaId(),
//   getRecipe(), updateRecipe(), getHistory(), clearHistory(),
//   sendMessage(), confirmModification(), isFreeTier()

import { RecipeFormData } from '@/types/recipe';
import {
    StudioChatMessage,
    StudioChatResponse,
    StudioChatConfig,
    StudioIntent,
    ModificationResult
} from './types';
import { ChatService } from '@/services/chat';
import { loadPersonaChatProfile } from './personaLoader';
import { getPersonaWelcome, getPersonaIntro } from './welcomeMessages';
import { handleRecipeModificationRequest } from '@/services/recipeService';

export class StudioChatService {
    private chat: ChatService;
    private personaId: string;
    private recipe: RecipeFormData;

    constructor(config: StudioChatConfig) {
        this.personaId = config.personaId;
        this.recipe = config.recipe;

        this.chat = new ChatService({
            personaId: config.personaId,
            personaChatProfile: loadPersonaChatProfile(config.personaId),
            recipe: config.recipe,
            // Studio recipes carry dietary tags in the `tags` field. Passing
            // them through lets the chat prompt flag conflicts (e.g. swapping
            // tofu for chicken on a vegetarian recipe).
            dietaryTags: config.recipe?.tags
        });
    }

    async initialize(): Promise<void> {
        return this.chat.initialize();
    }

    getWelcomeMessage(): string {
        return getPersonaWelcome(this.personaId, this.recipe);
    }

    getPersonaIntro(): string {
        return getPersonaIntro(this.personaId);
    }

    getPersonaId(): string {
        return this.personaId;
    }

    getRecipe(): RecipeFormData {
        return this.recipe;
    }

    updateRecipe(recipe: RecipeFormData): void {
        this.recipe = recipe;
        this.chat.updateRecipe(recipe);
    }

    getHistory(): StudioChatMessage[] {
        return this.chat.getHistory();
    }

    clearHistory(): void {
        this.chat.clearHistory();
    }

    sendMessage(userMessage: string): Promise<StudioChatResponse> {
        return this.chat.sendMessage(userMessage);
    }

    /**
     * Confirm and execute a recipe modification. Studio-specific concern —
     * not part of the generic ChatService.
     *
     * @param modificationRequest The user's modification request (possibly
     *   built from prior context by the chat classifier)
     * @param options.preserveImageUrl Optional image URL to keep on the
     *   updated recipe (modification handlers return text-only form data,
     *   so the caller must opt in to re-attaching an image)
     */
    async confirmModification(
        modificationRequest: string,
        options?: { preserveImageUrl?: string }
    ): Promise<ModificationResult> {
        try {
            const result = await handleRecipeModificationRequest(this.recipe, modificationRequest, undefined, this.personaId);

            if (result.success && result.data?.formData) {
                const updatedFormData = options?.preserveImageUrl
                    ? { ...result.data.formData, image_url: options.preserveImageUrl }
                    : result.data.formData;

                this.recipe = updatedFormData;
                this.chat.updateRecipe(updatedFormData);

                // Add both turns to history via the chat service
                this.chat.addHistoryEntry('user', modificationRequest, 'CONFIRM_MODIFY');
                const summary = result.data.changesSummary || 'Recipe has been updated!';
                this.chat.addHistoryEntry('chef', summary);

                return {
                    success: true,
                    updatedRecipe: updatedFormData,
                    changesSummary: summary
                };
            }

            return {
                success: false,
                error: result.error || 'Failed to modify recipe'
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Modification failed'
            };
        }
    }

    isFreeTier(intent: StudioIntent): boolean {
        return this.chat.isFreeTier(intent);
    }
}

/**
 * Factory function to create a StudioChatService instance.
 */
export function createStudioChatService(config: StudioChatConfig): StudioChatService {
    return new StudioChatService(config);
}
